import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Phone, Lock, ArrowRight } from "lucide-react";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

export default function Login() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [forgot, setForgot] = useState(false);
  const [resetPhone, setResetPhone] = useState("");
  const [resetSent, setResetSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(phone.trim(), password);
      toast.success("Welcome back to VirtualWin!");
      navigate(u.role === "admin" ? "/admin" : "/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed.");
    } finally {
      setLoading(false);
    }
  };

  const sendReset = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/reset-request", { phone: resetPhone.trim() });
      setResetSent(true);
      toast.success(data.message);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit request.");
    } finally { setLoading(false); }
  };

  if (forgot) {
    return (
      <AuthShell>
        <h1 className="font-display text-3xl font-black text-white">Forgot password</h1>
        {resetSent ? (
          <div className="mt-5" data-testid="reset-sent">
            <p className="text-slate-400">An administrator will reset your password. Please contact support to receive your new password, then log in.</p>
            <button onClick={() => { setForgot(false); setResetSent(false); }} className="vw-btn-primary mt-6 inline-flex items-center gap-2" data-testid="reset-back-login">
              Back to Login <ArrowRight size={18} />
            </button>
          </div>
        ) : (
          <>
            <p className="mt-2 text-slate-400 text-sm">Enter your phone number. An administrator will reset your password (no OTP).</p>
            <form onSubmit={sendReset} className="mt-7 space-y-4">
              <Field icon={Phone} label="Phone number" value={resetPhone} onChange={setResetPhone} placeholder="0244123456" testid="reset-phone" />
              <button disabled={loading} className="vw-btn-primary w-full !py-3.5 flex items-center justify-center gap-2 disabled:opacity-60" data-testid="reset-submit">
                {loading ? "Submitting…" : <>Request Reset <ArrowRight size={18} /></>}
              </button>
            </form>
            <button onClick={() => setForgot(false)} className="mt-6 text-sm text-emerald-400 font-semibold w-full text-center" data-testid="reset-cancel">Back to Login</button>
          </>
        )}
      </AuthShell>
    );
  }

  return (
    <AuthShell>
      <h1 className="font-display text-3xl font-black text-white">Welcome back</h1>
      <p className="mt-2 text-slate-400 text-sm">Log in to access your VirtualWin dashboard.</p>
      <form onSubmit={submit} className="mt-7 space-y-4">
        <Field icon={Phone} label="Phone number" value={phone} onChange={setPhone} placeholder="0244123456" testid="login-phone" />
        <Field icon={Lock} label="Password" type="password" value={password} onChange={setPassword} placeholder="••••••••" testid="login-password" />
        <div className="flex justify-end -mt-1">
          <button type="button" onClick={() => setForgot(true)} className="text-xs text-emerald-400 font-semibold" data-testid="login-forgot">Forgot password?</button>
        </div>
        <button disabled={loading} className="vw-btn-primary w-full !py-3.5 flex items-center justify-center gap-2 disabled:opacity-60" data-testid="login-submit">
          {loading ? "Logging in…" : <>Login <ArrowRight size={18} /></>}
        </button>
      </form>
      <p className="mt-6 text-sm text-slate-400 text-center">
        No account yet? <Link to="/register" className="text-emerald-400 font-semibold" data-testid="login-to-register">Create one</Link>
      </p>
    </AuthShell>
  );
}

export const AuthShell = ({ children }) => (
  <div className="min-h-screen grid lg:grid-cols-2 bg-[#0B0F17]">
    <div className="hidden lg:block relative pitch-gradient">
      <img src="https://images.unsplash.com/photo-1556056504-5c7696c4c28d?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200" alt="" className="absolute inset-0 w-full h-full object-cover opacity-25" />
      <div className="absolute inset-0" style={{ background: "linear-gradient(180deg,rgba(11,15,23,0.6),rgba(11,15,23,0.95))" }} />
      <div className="relative h-full flex flex-col justify-between p-12">
        <Logo size="lg" />
        <div>
          <h2 className="font-display text-4xl font-black text-white leading-tight">Upload. Analyze.<br /><span style={{ color: "#00FF66" }}>Predict. ⚡</span></h2>
          <p className="mt-4 text-slate-300 max-w-md">Turn your virtual football fixture screenshots into focused Home Win, Draw and Away Win selections.</p>
        </div>
      </div>
    </div>
    <div className="flex items-center justify-center p-6 sm:p-10">
      <div className="w-full max-w-md vw-fade-up">
        <div className="lg:hidden mb-8"><Logo size="lg" /></div>
        {children}
      </div>
    </div>
  </div>
);

export const Field = ({ icon: Icon, label, value, onChange, type = "text", placeholder, testid }) => (
  <div>
    <label className="block text-sm font-medium text-slate-300 mb-1.5">{label}</label>
    <div className="relative">
      <Icon size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-500" />
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
        className="vw-input !pl-11" data-testid={testid} required />
    </div>
  </div>
);
